import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { ExternalLink, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { getNewsUpdates, deleteNewsUpdates } from "../../../services/newsupdates";
import { Link, useNavigate } from "react-router-dom";

interface NewsUpdate {
    id: number;
    title: string;
    slug: string;
    content: string;
    status: string;
    created_at: string;
}

function AdminNewsUpdates() {
    const navigate = useNavigate();
    const [newsUpdates, setNewsUpdates] = useState<NewsUpdate[]>([]);
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    const loadNews = async () => {
        setIsLoading(true);
        const res = await getNewsUpdates();
        if (res.success && Array.isArray(res.data)) {
            const sorted = [...res.data].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            setNewsUpdates(sorted);
        } else {
            console.error("Failed to load news", res.error || res.data);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadNews();
    }, []);

    const handleDelete = async (id: number) => {
        const confirm = window.confirm("Are you sure you want to delete this news update?");
        if (!confirm) return;

        const res = await deleteNewsUpdates(id);
        if (res.success) {
            setNewsUpdates((prev) => prev.filter((news) => news.id !== id));
        } else {
            alert("Failed to delete: " + res.error);
        }
    };

    const handleEditClick = (id: number) => {
        navigate(`/admin/news-updates/edit-news-updates/${id}`);
    };

    const filteredNews = newsUpdates.filter((n) =>
        n.title.toLowerCase().includes(search.toLowerCase())
    );

    const columns: TableColumn<NewsUpdate>[] = [
        {
            name: "ID",
            cell: (_row, index) => (index + 1).toString(),
            width: "60px",
        },
        {
            name: "Title",
            selector: (row) => row.title,
            sortable: true,
        },
        {
            name: "Slug",
            selector: (row) => row.slug,
            sortable: true,
        },
        {
            name: "Status",
            cell: (row) => (
                <span
                    className={`px-2 py-1 text-xs font-medium rounded ${row.status === "publish"
                        ? "text-green-700 bg-green-100"
                        : "text-red-700 bg-red-100"
                        }`}
                >
                    {row.status}
                </span>
            ),
        },
        {
            name: "Created",
            selector: (row) =>
                new Date(row.created_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                }),
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => window.open(`/news-updates-details/${row.slug}`, '_blank')}
                        className="text-black hover:underline"
                        title="View"
                    >
                        <ExternalLink size={16} />
                    </button>
                    <button
                        onClick={() => handleEditClick(row.id)}
                        className="text-[#009FE3] hover:underline"
                        title="Edit"
                    >
                        <Pencil size={16} />
                    </button>
                    <button
                        onClick={() => handleDelete(row.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>

            ),
            width: "150px",
        },
    ];

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-semibold text-gray-800">News & Updates</h1>
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search news..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#009FE3]"
                    />
                </div>

                <Link
                    to="add-news-updates"
                    className="flex items-center gap-2 px-4 py-2 bg-[#009FE3] text-white rounded-lg hover:opacity-90 transition whitespace-nowrap"
                >
                    <Plus className="w-4 h-4" />
                    Add News
                </Link>
            </div>
            <div className="bg-white rounded-lg shadow border">
                <DataTable
                    columns={columns}
                    data={filteredNews}
                    progressPending={isLoading}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    noHeader
                />
            </div>
        </div>
    );
}

export default AdminNewsUpdates;
